import { useEffect, useRef, useState } from 'react'

import { sfx } from '../../../lib/sound'
import type { GameProps } from '../GameShell'

const W = 340
const H = 460
const BLOCK_H = 22
const START_W = 150
const BASE_SPEED = 2.2

interface Block {
  x: number
  w: number
}

interface TowerState {
  stack: Block[]
  moving: Block
  direction: 1 | -1
  speed: number
  /** How far the camera has scrolled up, in pixels. */
  camera: number
  running: boolean
  height: number
}

const COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#06b6d4',
]

export default function Tower({ onGameOver, onScore }: GameProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [height, setHeight] = useState(0)
  const [perfect, setPerfect] = useState(0)

  const state = useRef<TowerState>({
    stack: [{ x: (W - START_W) / 2, w: START_W }],
    moving: { x: 0, w: START_W },
    direction: 1,
    speed: BASE_SPEED,
    camera: 0,
    running: true,
    height: 0,
  })

  // Dropping the block: the only input the game has.
  useEffect(() => {
    function place() {
      const s = state.current
      if (!s.running) return

      const below = s.stack[s.stack.length - 1]
      const left = Math.max(s.moving.x, below.x)
      const right = Math.min(s.moving.x + s.moving.w, below.x + below.w)
      const overlap = right - left

      if (overlap <= 0) {
        // Missed entirely.
        s.running = false
        sfx.thud()
        onGameOver(s.height)
        return
      }

      // The overhang is sliced off, so the tower narrows with every sloppy
      // drop and eventually becomes impossible -- that is the whole game.
      const trimmed: Block = { x: left, w: overlap }
      s.stack.push(trimmed)
      s.height += 1
      setHeight(s.height)
      onScore?.(s.height)

      const wasPerfect = Math.abs(overlap - s.moving.w) < 2
      if (wasPerfect) {
        setPerfect((p) => p + 1)
        sfx.combo(3)
      } else {
        sfx.pop()
      }

      if (trimmed.w < 8) {
        s.running = false
        sfx.thud()
        onGameOver(s.height)
        return
      }

      // Next block enters from the alternating side, slightly faster.
      s.direction = s.direction === 1 ? -1 : 1
      s.speed = Math.min(7, BASE_SPEED + s.height * 0.12)
      s.moving = {
        x: s.direction === 1 ? -trimmed.w : W,
        w: trimmed.w,
      }
      s.camera += BLOCK_H
    }

    function onKey(event: KeyboardEvent) {
      if (event.key === ' ' || event.key === 'ArrowDown' || event.key === 'Enter') {
        event.preventDefault()
        place()
      }
    }

    const canvas = canvasRef.current
    window.addEventListener('keydown', onKey)
    canvas?.addEventListener('pointerdown', place)
    return () => {
      window.removeEventListener('keydown', onKey)
      canvas?.removeEventListener('pointerdown', place)
    }
  }, [onGameOver, onScore])

  useEffect(() => {
    const maybeCtx = canvasRef.current?.getContext('2d')
    if (!maybeCtx) return
    // Rebound with an explicit type: the narrowing from the guard above does
    // not reach inside the hoisted `step` function below.
    const ctx: CanvasRenderingContext2D = maybeCtx
    let frame = 0

    function step() {
      const s = state.current
      if (!s.running) return

      s.moving.x += s.speed * s.direction
      // Bounce off the edges rather than wrapping, so the block is always
      // visible and the timing is readable.
      if (s.moving.x + s.moving.w > W) {
        s.moving.x = W - s.moving.w
        s.direction = -1
      } else if (s.moving.x < 0) {
        s.moving.x = 0
        s.direction = 1
      }

      draw(ctx, s)
      frame = requestAnimationFrame(step)
    }

    frame = requestAnimationFrame(step)
    // Captured rather than read in cleanup: the ref object never changes, but
    // reading `.current` at teardown is the pattern that bites when it does.
    const game = state.current
    return () => {
      cancelAnimationFrame(frame)
      game.running = false
    }
  }, [])

  return (
    <div className="space-y-3">
      <p className="text-center text-sm text-muted">
        Height {height}
        {perfect > 0 && ` · ${perfect} perfect`} · tap or space to drop
      </p>
      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        className="mx-auto w-full max-w-xs cursor-pointer touch-none rounded-xl border border-border-subtle bg-canvas"
      />
    </div>
  )
}

function draw(ctx: CanvasRenderingContext2D, s: TowerState) {
  ctx.clearRect(0, 0, W, H)

  // The camera rises with the stack, so the top is always in view.
  const baseY = H - BLOCK_H + s.camera

  s.stack.forEach((block, i) => {
    const y = baseY - i * BLOCK_H - s.camera
    if (y < -BLOCK_H || y > H) return
    ctx.fillStyle = COLORS[i % COLORS.length]
    ctx.fillRect(block.x, y, block.w, BLOCK_H - 2)
  })

  // The block still in motion sits one level above the top of the stack.
  const movingY = baseY - s.stack.length * BLOCK_H - s.camera
  ctx.fillStyle = COLORS[s.stack.length % COLORS.length]
  ctx.fillRect(s.moving.x, movingY, s.moving.w, BLOCK_H - 2)

  // A faint guide showing the target width.
  const top = s.stack[s.stack.length - 1]
  ctx.strokeStyle = 'rgba(148,163,184,0.35)'
  ctx.setLineDash([4, 4])
  ctx.beginPath()
  ctx.moveTo(top.x, movingY)
  ctx.lineTo(top.x, movingY + BLOCK_H)
  ctx.moveTo(top.x + top.w, movingY)
  ctx.lineTo(top.x + top.w, movingY + BLOCK_H)
  ctx.stroke()
  ctx.setLineDash([])
}
